import { applyAccessibleToggleDanger } from './applyAccessibleToggleDanger';
import {
  applyChatPanelBrandOverride,
  applyChatSelectorUiTokens,
  applyMessageViewBrandOverrides,
  applySetupScreenJoinButtonTheme,
} from './applyChatPanelBrandOverride';
import {
  applyDialogChromeContrast,
  applyPrimaryMenuListTextContrast,
  applySidebarUiBrandRampForContrast,
  applyStageAndSidebarLeafPatches,
  ensureSidebarUiHeaderChromeContrast,
} from './applyRtkSidebarAndStageContrast';
import { ensureRtkMeetingTextContrast } from './ensureRtkMeetingTextContrast';
import {
  collectEndedScreenDesignSystemTargets,
  collectSetupScreenDesignSystemTargets,
  collectSidebarDesignSystemTargets,
} from './meetingTheme';
import { injectRtkDebuggerLayoutOverrides } from './injectRtkDebuggerLayoutOverrides';

/**
 * Sidebar/chat/setup/ended live under nested shadow roots; outer `MutationObserver` does not see DOM
 * changes inside those trees (e.g. chat toggled closed/open recreates `rtk-chat` / `rtk-message-view`).
 * Call from delayed timeouts, roomJoined, and a periodic tick so remounted UI keeps WCAG + brand tokens.
 *
 * @param {HTMLElement | null} meetingHostEl outer `<rtk-meeting>`
 * @param {object | null | undefined} themeDetails from `getMeetingThemeDetails`
 */
export function applyMeetingScopedPanelTheme(meetingHostEl, themeDetails) {
  if (!meetingHostEl) return;

  const headerColor =
    themeDetails?.headerColor ?? themeDetails?.presetTheme?.headerColor;
  const headerBackground =
    themeDetails?.headerBackground ?? themeDetails?.presetTheme?.headerBackground;
  const buttonBackground =
    themeDetails?.buttonBackground ?? themeDetails?.presetTheme?.buttonBackground;

  const preset = themeDetails?.presetTheme ?? {};
  const preferredUiForeground =
    themeDetails?.headerColor ??
    preset.headerColor ??
    themeDetails?.buttonColor ??
    preset.buttonColor;

  const targets = [
    ...collectSidebarDesignSystemTargets(meetingHostEl),
    ...collectSetupScreenDesignSystemTargets(meetingHostEl),
  ];
  for (const el of targets) {
    ensureRtkMeetingTextContrast(el, {
      preferredTextHex: headerColor,
      headerBackground,
      surfaceBackgroundHex: headerBackground,
    });
    if (el.localName === 'rtk-sidebar-ui') {
      ensureSidebarUiHeaderChromeContrast(el);
      applySidebarUiBrandRampForContrast(el, themeDetails);
    }
    if (el.localName === 'rtk-chat') {
      applyChatPanelBrandOverride(el, themeDetails);
    }
  }

  for (const el of collectEndedScreenDesignSystemTargets(meetingHostEl)) {
    ensureRtkMeetingTextContrast(el, {
      preferredTextHex: preferredUiForeground || '#000000',
      headerBackground,
      surfaceBackgroundHex: headerBackground,
    });
  }

  applyChatSelectorUiTokens(meetingHostEl, themeDetails);
  applyMessageViewBrandOverrides(meetingHostEl, themeDetails);
  applySetupScreenJoinButtonTheme(meetingHostEl, themeDetails);
  applyAccessibleToggleDanger(meetingHostEl, buttonBackground);
  applyDialogChromeContrast(meetingHostEl, themeDetails);
  applyPrimaryMenuListTextContrast(meetingHostEl, themeDetails);
  applyStageAndSidebarLeafPatches(meetingHostEl, themeDetails);
  injectRtkDebuggerLayoutOverrides(meetingHostEl);
}
